/* DD Kit Builder.
   Tapping product cards builds a live receipt with a free-shipping meter, then
   "Add kit to cart" adds every selected variant in one /cart/add.js call and
   opens the drawer — mirroring the theme's product-form success flow
   (renderContents + mini-cart.open). */
if (!customElements.get('dd-kit-builder')) {
  class DDKitBuilder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.bound) return;
      this.dataset.bound = '1';

      this.threshold = Number(this.dataset.threshold) || 0; // cents
      this.readyLabel = this.dataset.ctaReady || 'Add kit to cart';
      this.inputs = Array.from(this.querySelectorAll('.dd-kit__input'));
      this.linesEl = this.querySelector('[data-kit-lines]');
      this.countEl = this.querySelector('[data-kit-count]');
      this.totalEl = this.querySelector('[data-kit-total]');
      this.meterEl = this.querySelector('[data-kit-meter]');
      this.fillEl = this.querySelector('[data-kit-fill]');
      this.meterText = this.querySelector('[data-kit-meter-text]');
      this.cta = this.querySelector('[data-kit-cta]');
      this.ctaLabel = this.querySelector('[data-kit-cta-label]');
      this.emptyText = this.linesEl ? this.linesEl.innerHTML : '';
      this.currentCents = 0;
      this.totalRaf = null;
      this.reduceMotion =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.inputs.forEach((i) => {
        i.addEventListener('change', () => {
          i.closest('.dd-kit__item').classList.toggle('is-checked', i.checked);
          this.render();
        });
      });
      if (this.cta) this.cta.addEventListener('click', this.addKit.bind(this));

      this.render();
    }

    money(cents) {
      return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    }

    /* Counts the subtotal from its current value to the new one so the digits
       roll up (adds) or down (removes) instead of snapping. easeOutCubic so it
       settles; interruptible; honors prefers-reduced-motion. */
    animateTotal(toCents) {
      if (this.totalRaf) cancelAnimationFrame(this.totalRaf);
      const from = this.currentCents;
      if (this.reduceMotion || from === toCents) {
        this.currentCents = toCents;
        this.totalEl.textContent = this.money(toCents);
        return;
      }
      const dur = 450;
      const start = performance.now();
      const step = (now) => {
        const p = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        const val = Math.round(from + (toCents - from) * eased);
        this.currentCents = val;
        this.totalEl.textContent = this.money(val);
        if (p < 1) {
          this.totalRaf = requestAnimationFrame(step);
        } else {
          this.currentCents = toCents;
          this.totalEl.textContent = this.money(toCents);
          this.totalRaf = null;
        }
      };
      this.totalRaf = requestAnimationFrame(step);
    }

    selected() {
      return this.inputs.filter((i) => i.checked);
    }

    render() {
      const sel = this.selected();
      const subtotal = sel.reduce((s, i) => s + Number(i.dataset.price || 0), 0);

      // Receipt lines
      if (sel.length === 0) {
        this.linesEl.innerHTML = this.emptyText;
      } else {
        this.linesEl.innerHTML = sel
          .map(
            (i) =>
              `<li><span>${this.escape(i.dataset.name)}</span><span>${this.money(
                Number(i.dataset.price || 0)
              )}</span></li>`
          )
          .join('');
      }

      this.countEl.textContent = `${sel.length} item${sel.length === 1 ? '' : 's'}`;
      this.animateTotal(subtotal);

      // Meter
      if (this.threshold > 0) {
        const pct = Math.min(100, (subtotal / this.threshold) * 100);
        this.fillEl.style.width = pct + '%';
        const free = subtotal >= this.threshold;
        this.meterEl.classList.toggle('is-free', free);
        this.meterText.textContent = free
          ? 'Free shipping unlocked.'
          : `Add ${this.money(this.threshold - subtotal)} to unlock free shipping`;
      }

      // CTA
      const has = sel.length > 0;
      this.cta.disabled = !has;
      this.cta.setAttribute('aria-disabled', has ? 'false' : 'true');
      this.ctaLabel.textContent = has ? `Add ${sel.length} to cart` : this.readyLabel;
    }

    escape(s) {
      const d = document.createElement('div');
      d.textContent = s == null ? '' : s;
      return d.innerHTML;
    }

    sectionIds() {
      const ids = [];
      const header = document.getElementById('header');
      if (header && header.dataset.id) ids.push(header.dataset.id);
      ids.push('header-mini-cart-content', 'header-mini-cart-footer');
      const dock = document.getElementById('mobile-dock');
      if (dock && dock.dataset.id) ids.push(dock.dataset.id);
      return ids.join(',');
    }

    setBusy(busy) {
      this.cta.classList.toggle('is-loading', busy);
      this.cta.disabled = busy || this.selected().length === 0;
    }

    addKit() {
      const sel = this.selected();
      if (sel.length === 0) return;
      this.setBusy(true);

      const payload = {
        items: sel.map((i) => ({ id: Number(i.dataset.id), quantity: 1 })),
        sections: this.sectionIds(),
        sections_url: window.location.pathname,
      };

      fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      })
        .then((r) => r.json())
        .then((response) => {
          if (response.status) {
            // Shopify returns status/description on error (e.g. sold out).
            console.error('dd-kit-builder', response.description || response.message);
            this.setBusy(false);
            return;
          }

          const mc = document.querySelector('mini-cart');
          if (mc && typeof mc.renderContents === 'function' && response.sections) {
            mc.renderContents(response);
            if (typeof mc.open === 'function') {
              document.body.classList.add('drawer-open');
              setTimeout(() => mc.open(this.cta), 150);
            }
          } else {
            window.location.href = (window.routes && window.routes.cart_url) || '/cart';
            return;
          }

          document.dispatchEvent(
            new CustomEvent('cart:item-add', { detail: { items: [response], context: 'dd-kit-builder' } })
          );

          // Clear the picks so a second click can't silently double-add.
          this.inputs.forEach((i) => {
            i.checked = false;
            i.closest('.dd-kit__item').classList.remove('is-checked');
          });
          this.render();
          this.setBusy(false);
        })
        .catch((e) => {
          console.error('dd-kit-builder', e);
          this.setBusy(false);
        });
    }
  }

  customElements.define('dd-kit-builder', DDKitBuilder);
}
